import type { ILineItemInput } from '@commenda/api/new-sales-tax/calculate/calculate';
import { calculateSalesTax } from '@commenda/api/new-sales-tax/calculate/calculate';
import {
  AddressInput,
  Button,
  Input,
  Label,
  LoadingIndicator,
  SingleCombobox,
  Textarea,
} from '@commenda/kakashi';
import { IAddress } from '@commenda/types';
import { useState } from 'react';
import { AsyncCombobox } from '../../AsyncCombobox/AsyncCombobox';
import { useCustomerSearch } from '../exemptions-tab/customers/useCustomerSearch';
import { LineItemsEditor } from './LineItemsEditor';

const EMPTY_ADDRESS: IAddress = {
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  postalCode: '',
  city: '',
  state: '',
  country: 'US',
};

export default function SalesTaxCalculatorTab({
  corporationId,
}: {
  corporationId: number;
}) {
  const [shipTo, setShipTo] = useState<IAddress>(EMPTY_ADDRESS);
  const [lineItems, setLineItems] = useState<ILineItemInput[] | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [resultText, setResultText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [errorBodyText, setErrorBodyText] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [transactionDate, setTransactionDate] = useState<string>(
    new Date().toISOString().slice(0, 10)
  );
  const [transactionCurrency, setTransactionCurrency] = useState<string>('USD');
  const [transactionType, setTransactionType] = useState<
    'SALE' | 'REFUND' | 'RETURN'
  >('SALE');

  const {
    customers,
    isLoading: isLoadingCustomers,
    handleSearch,
    loadMore,
    hasMore,
  } = useCustomerSearch({ corporationId });

  const handleCalculate = async () => {
    setIsLoading(true);
    setResultText('');
    setErrorText('');
    setErrorBodyText('');
    const res = await calculateSalesTax({
      shipTo,
      corporationId,
      lineItems: lineItems && lineItems.length > 0 ? lineItems : undefined,
      customerDetails: {
        customerId: customerId || '',
      },
      transactionDate,
      transactionCurrency,
      transactionType,
    });
    if (res.success) {
      setResultText(JSON.stringify(res.data, null, 2));
    } else {
      setErrorText(res.error || 'Failed to calculate sales tax');
      setErrorBodyText(
        res.errorBody !== undefined
          ? JSON.stringify(res.errorBody, null, 2)
          : ''
      );
    }
    setIsLoading(false);
  };

  return (
    <div className="w-full h-full overflow-y-auto mt-3xl">
      <div className="w-full mb-4 rounded border border-yellow-200 bg-yellow-50 px-3 py-2">
        <p className="txt-small-regular text-yellow-900">
          Internal calculations tester is in beta. This UI only tests the tax
          engine, any bugs in the calculate flow in integrations will not be
          captured here.
        </p>
      </div>
      <div className="max-w-3xl flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="txt-large-bold text-text-primary">Tax Calculator</h2>
          <p className="txt-small-regular text-text-tertiary">
            Enter a shipping address and calculate tax. Response JSON will
            appear below.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mt-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="txt-medium-medium text-text-primary">
            Customer (optional)
          </h3>
        </div>
        <AsyncCombobox
          onSearch={handleSearch}
          isLoading={isLoadingCustomers}
          onLoadMore={loadMore}
          hasMore={hasMore}
          options={customers}
          value={customerId}
          setValue={setCustomerId}
          placeholder="Select customer"
          canClearSelection
          _className="txt-medium-regular px-3 border-border-primary"
          renderOption={({ option, isSelected }) => (
            <div className="flex items-center justify-between w-full">
              <span className="txt-medium-regular text-text-primary truncate">
                {option.label}
              </span>
              <div className="w-4 shrink-0 ml-2">
                {isSelected && <span>✓</span>}
              </div>
            </div>
          )}
        />
      </div>

      <div className="max-w-3xl mt-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="txt-medium-medium text-text-primary">Address</h3>
        </div>
        <AddressInput address={shipTo} onChange={setShipTo} />
      </div>

      <div className="w-full mt-4 flex flex-col gap-3">
        <LineItemsEditor
          value={lineItems}
          onChange={setLineItems}
          corporationId={corporationId}
        />
      </div>

      <div className="max-w-3xl mt-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="txt-medium-medium text-text-primary">
            Advanced Fields
          </h3>
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => setIsAdvancedOpen((v) => !v)}
          >
            {isAdvancedOpen ? 'Hide' : 'Show'}
          </Button>
        </div>
        {isAdvancedOpen ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Transaction date</Label>
              <Input
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Transaction currency</Label>
              <Input
                value={transactionCurrency}
                onChange={(e) =>
                  setTransactionCurrency(e.target.value.toUpperCase())
                }
                placeholder="USD"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Transaction type</Label>
              <SingleCombobox
                options={[
                  { value: 'SALE', label: 'Sale' },
                  { value: 'REFUND', label: 'Refund' },
                  { value: 'RETURN', label: 'Return' },
                ]}
                value={transactionType}
                setValue={(val) =>
                  setTransactionType(
                    (val as 'SALE' | 'REFUND' | 'RETURN') || 'SALE'
                  )
                }
                placeholder="Select type"
                _className="txt-small-medium"
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="max-w-3xl mt-4 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button onClick={handleCalculate} disabled={isLoading}>
            Calculate
          </Button>
          {isLoading ? <LoadingIndicator size={20} /> : null}
        </div>

        {errorText ? (
          <div className="flex flex-col gap-2">
            <div className="text-red-600 txt-small-regular">{errorText}</div>
            {errorBodyText ? (
              <div className="flex flex-col gap-1">
                <label className="txt-small-medium text-text-secondary">
                  Error response
                </label>
                <Textarea readOnly value={errorBodyText} className="min-h-64" />
              </div>
            ) : null}
          </div>
        ) : null}

        {resultText ? (
          <div className="flex flex-col gap-2">
            <label className="txt-small-medium text-text-secondary">
              Response
            </label>
            <Textarea readOnly value={resultText} className="min-h-64" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
